import { unwrap } from "@sagot/api-client";
import { PII_KINDS, PII_PREVIEW_MAX_CHARS, consoleRoutes, type PiiKindKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, EmptyState, ErrorState, Field, Icon, Skeleton, Switch, Textarea, buttonClasses, toast } from "@sagot/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useDebounced } from "../../../../lib/slug";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { useAgentList, wsKeys, type PrivacyOut } from "../../shared";
import { useInvalidate } from "../use-settings";
import { CardActions, useCanManage } from "./panel-kit";
import { kindsChanged, kindsOf, kindsOn, maskParts, type Kinds } from "./redaction-model";

/** `POST /v1/agents/{id}/privacy/redaction-preview` with the kinds being edited (M29 E27). The text is
 * neither stored by the server nor kept by the cache once the card closes. */
function useRedactionPreview(agentId: string | undefined, text: string, kinds: PiiKindKey[]) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const settled = useDebounced(text);
  return useQuery({
    queryKey: ["ws", wsId, "settings", "redaction-preview", agentId, settled, kinds.join(",")] as const,
    queryFn: () =>
      unwrap(api.POST("/v1/agents/{agent_id}/privacy/redaction-preview", { params: { path: { agent_id: agentId ?? "" } }, body: { text: settled, kinds } })),
    enabled: agentId !== undefined && settled.trim() !== "",
    placeholderData: keepPreviousData,
    gcTime: 0,
    retry: false,
  });
}

/** The masked text with each mask set apart so it reads as "what changed". */
function MaskedText({ text }: { text: string }) {
  return (
    <>
      {maskParts(text).map((part, index) =>
        part.isMask ? (
          <mark key={index} className="rounded-sm bg-primary-soft px-1 font-semibold text-primary-ink">
            {part.text}
          </mark>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

function KindRow({ kind, checked, disabled, onChange }: { kind: PiiKindKey; checked: boolean; disabled: boolean; onChange: (on: boolean) => void }) {
  const { t } = useTranslation();
  const labelId = `redaction-kind-${kind}`;
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <div className="min-w-0">
        <div id={labelId} className="text-sm font-semibold">
          {t(`privacyM29.redaction.kinds.${kind}.label`)}
        </div>
        <div className="text-xs text-fg-muted">{t(`privacyM29.redaction.kinds.${kind}.hint`)}</div>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} aria-labelledby={labelId} data-testid={`redaction-kind-${kind}`} />
    </div>
  );
}

function PreviewOutput({ agentId, text, kinds }: { agentId: string; text: string; kinds: PiiKindKey[] }) {
  const { t } = useTranslation();
  const preview = useRedactionPreview(agentId, text, kinds);
  const settledEmpty = text.trim() === "";
  if (settledEmpty) return <p className="text-xs text-fg-muted">{t("privacyM29.redaction.previewEmpty")}</p>;
  if (preview.isError) {
    return <ErrorState headingLevel={3} title={t("privacyM29.redaction.previewFailedTitle")} description={t("privacyM29.redaction.previewFailedBody")} onRetry={() => void preview.refetch()} retryLabel={t("common.retry")} />;
  }
  if (!preview.data) {
    return (
      <div className="space-y-2" role="status" aria-label={t("privacyM29.redaction.previewLoading")}>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }
  const count = preview.data.matches.length;
  return (
    <div className="space-y-2">
      {/* The result is announced when it changes (the masked text is what the model receives). */}
      <div role="status" aria-live="polite" aria-atomic="true">
        <pre data-testid="redaction-preview" className="whitespace-pre-wrap break-words rounded-md border border-line bg-surface-2 p-3 font-mono text-xs text-fg">
          <MaskedText text={preview.data.redacted} />
        </pre>
        <p className="mt-2 text-xs text-fg-muted" data-testid="redaction-preview-count">
          {count > 0 ? t("privacyM29.redaction.masked", { count }) : t("privacyM29.redaction.previewNothing")}
        </p>
      </div>
    </div>
  );
}

function PreviewSection({ kinds }: { kinds: PiiKindKey[] }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agents = useAgentList();
  const [text, setText] = useState(t("privacyM29.redaction.previewSample"));
  const agentId = agents.data?.[0]?.id;
  // A longer text is kept as typed and flagged, never cut: the server refuses it (422), so no preview is asked for.
  const tooLong = text.length > PII_PREVIEW_MAX_CHARS;
  const limit = PII_PREVIEW_MAX_CHARS.toLocaleString("en-US");
  if (agents.isPending) return <Skeleton className="h-24 w-full" />;
  if (agents.isError || agentId === undefined) {
    return (
      <EmptyState
        icon="bot"
        headingLevel={3}
        title={t("privacyM29.redaction.noAssistantTitle")}
        description={t("privacyM29.redaction.noAssistantBody")}
        action={
          <AppLink href={consoleRoutes.agents(summary.slug)} className={buttonClasses({ variant: "secondary" })}>
            <Icon name="plus" />
            {t("privacyM29.redaction.noAssistantAction")}
          </AppLink>
        }
      />
    );
  }
  return (
    <div className="space-y-3">
      <Field
        label={t("privacyM29.redaction.previewInput")}
        hint={t("privacyM29.redaction.previewLimit", { used: text.length.toLocaleString("en-US"), max: limit })}
        error={tooLong ? t("privacyM29.redaction.previewTooLong", { max: limit }) : undefined}
      >
        <Textarea
          rows={3}
          value={text}
          placeholder={t("privacyM29.redaction.previewPlaceholder")}
          onChange={(event) => { setText(event.target.value); }}
        />
      </Field>
      {tooLong ? null : <PreviewOutput agentId={agentId} text={text} kinds={kinds} />}
    </div>
  );
}

/** Settings › Privacy › "Redact before AI": which kinds of personal data are masked before a model
 * call, with a live preview of what the model receives (M29 WP-REDACT, SEC-001). */
export function RedactionCard({ privacy }: { privacy: PrivacyOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("settings.manage");
  const saved = kindsOf(privacy);
  const [draft, setDraft] = useState<Kinds>(saved);
  const [busy, setBusy] = useState(false);
  const on = kindsOn(draft);
  const dirty = kindsChanged(draft, saved);
  const locked = !manage.allowed;
  const save = async () => {
    setBusy(true);
    try {
      await unwrap(api.PUT("/v1/settings/privacy", { body: { retention_days: privacy.retention_days, attachment_retention_days: privacy.attachment_retention_days, pii_redaction: draft } }));
      toast.success(t("privacyM29.redaction.saved"));
      await invalidate(wsKeys.privacy(wsId));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card" aria-labelledby="redaction-title" data-testid="redaction-card">
      <div className="card-header flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="redaction-title" className="card-title">
            {t("privacyM29.redaction.title")}
          </h2>
          <p className="mt-1 text-xs text-fg-muted">{t("privacyM29.redaction.intro")}</p>
        </div>
        <Badge tone={on.length > 0 ? "success" : "warning"} dot data-testid="redaction-status">
          {on.length > 0 ? t("privacyM29.redaction.enforced") : t("privacyM29.redaction.off")}
        </Badge>
      </div>
      <fieldset className="divide-y divide-line" disabled={locked}>
        <legend className="sr-only">{t("privacyM29.redaction.kindsLabel")}</legend>
        {PII_KINDS.map((kind) => (
          <KindRow key={kind} kind={kind} checked={draft[kind]} disabled={locked} onChange={(next) => { setDraft((d) => ({ ...d, [kind]: next })); }} />
        ))}
      </fieldset>
      {on.length === 0 ? (
        <div className="flex items-start gap-2 border-t border-line bg-warning-soft px-5 py-3 text-xs text-fg" role="note">
          <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
          <span>{t("privacyM29.redaction.allOff")}</span>
        </div>
      ) : null}
      <div className="space-y-3 border-t border-line px-5 py-4">
        <h3 className="text-sm font-semibold">{t("privacyM29.redaction.previewTitle")}</h3>
        <PreviewSection kinds={on} />
        <p className="text-xs text-fg-muted">{t("privacyM29.redaction.agentNote")}</p>
      </div>
      <CardActions>
        <Button disabled={locked || !dirty} loading={busy} title={manage.reason} onClick={() => void save()}>
          {t("privacyM29.redaction.save")}
        </Button>
      </CardActions>
    </section>
  );
}
